import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useOnboardingFlow } from '../../../hooks/onboardingV2/useOnboardingFlow.ts';
import { useConversationalOnboarding } from '../../../hooks/onboardingV2/useConversationalOnboarding.ts';
import { useSummaryMode } from '../../../hooks/onboardingV2/useSummaryMode.ts';
import { OnboardingStep } from '../../../utils/onboardingV2/conversationalOnboardingSteps.ts';
import AgentMessage from './AgentMessage.tsx';
import ProfilePreview from './ProfilePreview.tsx';
import ChatInput from './ChatInput.tsx';
import InteractiveComponents from './InteractiveComponents.tsx';
import BackgroundEffect from '../../Background/BackgroundEffect.tsx';
import OnboardingNavigation from './OnboardingNavigation.tsx';
import styles from './ConversationalOnboarding.module.scss';

const ConversationalOnboarding: React.FC = () => {
  const {
    state,
    setAccountType,
    setPersonaForm,
    setEmpresaForm,
    setUserSkills,
    addSkill,
    removeSkill,
    setWorkZones,
    setAllCountry,
    setWorkCoverageKind,
    setInfluenceMap,
    setUserImage,
    updateProfile,
    loadSkillsAndCategories,
    getCities,
    setSelectedCategoryFilter,
    setPreferredProfileCategories,
    removePreferredProfileCategory,
  } = useOnboardingFlow();
  
  const {
    messages,
    currentQuestion,
    inputValue,
    setInputValue,
    handleSendMessage,
    handleInteractiveAction,
    handleSkipField,
    isProcessing,
    isTyping,
    shouldShowSkip,
    skipLabel,
    navigateToStep,
    handleInteractiveExitComplete,
    handleTypingComplete,
    showReturnToFinish,
    handleReturnToCompleteActions,
  } = useConversationalOnboarding({
    state,
    setAccountType,
    setPersonaForm,
    setEmpresaForm,
    setUserSkills,
    addSkill,
    removeSkill,
    setWorkZones,
    setAllCountry,
    setWorkCoverageKind,
    setInfluenceMap,
    setUserImage,
    setSelectedCategoryFilter,
    setPreferredProfileCategories,
    removePreferredProfileCategory,
    updateProfile,
    loadSkillsAndCategories,
    getCities,
  });

  const lastAssistantMessage = messages.length > 0 
    ? messages.filter(m => m.role === 'assistant').pop()
    : null;
  
  const currentAgentMessage = lastAssistantMessage?.content || '';

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (inputValue.trim() && !isProcessing) {
        handleSendMessage();
      }
    }
  };

  const canShowInput = currentQuestion?.type !== 'interactive' && !isTyping && currentAgentMessage && currentQuestion;
  const showInteractive =
    currentQuestion?.type === 'interactive' &&
    currentAgentMessage &&
    currentQuestion &&
    (!isTyping || currentQuestion.interactiveType === 'workZones');
  const interactiveDisabled = isTyping;
  const { summaryModeActive, isSummaryQuestion, handleSummaryExitComplete } = useSummaryMode({
    currentQuestion,
    onExitComplete: handleInteractiveExitComplete,
  });

  const editingStep: OnboardingStep | null = currentQuestion
    ? currentQuestion.id === 'accountType'
      ? 'accountType'
      : ['name', 'lastName', 'tel', 'webSite', 'bio', 'companyName'].includes(currentQuestion.id)
        ? 'basicData'
        : ['skills', 'profiles'].includes(currentQuestion.id)
          ? 'skills'
          : currentQuestion.id === 'workZones'
            ? 'workZones'
            : null
    : null;
  const totalSteps = 5;
  const stepLabels: Record<OnboardingStep, { step: number; label: string }> = {
    accountType: { step: 1, label: 'Tipo de cuenta' },
    basicData: { step: 2, label: 'Datos básicos' },
    skills: { step: 3, label: 'Habilidades' },
    workZones: { step: 4, label: 'Zonas de trabajo' },
    summary: { step: 5, label: 'Resumen' },
    complete: { step: 5, label: 'Finalizar' },
  };
  const mobileCtaConfig = !isTyping && currentQuestion?.type === 'interactive' ? (() => {
    switch (currentQuestion.interactiveType) {
      case 'skills':
        return {
          step: stepLabels.skills.step,
          label: stepLabels.skills.label,
          enabled: state.userSkills.length > 0,
          action: 'skillsComplete',
        };
      case 'profiles':
        return {
          step: stepLabels.skills.step,
          label: 'Perfiles',
          enabled: state.preferredProfileCategories.length > 0,
          action: 'profilesComplete',
        };
      case 'workZones':
        return {
          step: stepLabels.workZones.step,
          label: stepLabels.workZones.label,
          enabled: state.allCountry || state.workZones.length > 0,
          action: 'workZonesComplete',
        };
      case 'completeActions':
        return {
          step: stepLabels.workZones.step,
          label: 'Finalizar',
          enabled: true,
          action: 'finishOnboarding',
        };
      default:
        return null;
    }
  })() : null;
  const mobileSummaryConfig = !isTyping && currentQuestion?.type === 'interactive' && currentQuestion.interactiveType === 'summary'
    ? {
        step: stepLabels.summary.step,
        label: stepLabels.summary.label,
        primaryAction: 'confirmSummary',
        secondaryAction: 'summaryEdit',
      }
    : null;
  const mobileSkipConfig = canShowInput && shouldShowSkip
    ? (editingStep ? { ...stepLabels[editingStep], label: stepLabels[editingStep].label } : null)
    : null;

  return (
    <div className={`${styles.conversationalOnboarding} ${summaryModeActive ? styles.summaryMode : ''}`}>
      <BackgroundEffect />
      <div className={styles.mainContent}>
        <div className={styles.agentSection}>
          <div className={styles.messageArea}>
            <AnimatePresence mode="wait">
              {currentAgentMessage && !summaryModeActive && !isSummaryQuestion && (
                <AgentMessage 
                  key="agent-message"
                  content={currentAgentMessage} 
                  isTyping={isTyping}
                  messageId={lastAssistantMessage?.id}
                  onTypingComplete={handleTypingComplete}
                />
              )}
            </AnimatePresence>
          </div>

          <div className={styles.interactionArea}>
            <AnimatePresence mode="wait" onExitComplete={handleSummaryExitComplete}>
              {showInteractive && (
                <motion.div
                  key={`interactive-${currentQuestion?.id}`}
                  className={`${styles.interactiveSection} ${interactiveDisabled ? styles.interactiveSectionTyping : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  <InteractiveComponents
                    question={currentQuestion}
                    state={state}
                    onAction={handleInteractiveAction}
                    setSelectedCategoryFilter={setSelectedCategoryFilter}
                    onNavigateToStep={navigateToStep}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence mode="wait">
              {canShowInput && (
                <motion.div
                  key="input"
                  className={styles.inputSection}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  <ChatInput
                    value={inputValue}
                    onChange={setInputValue}
                    onSend={handleSendMessage}
                    onKeyPress={handleKeyPress}
                    disabled={isProcessing}
                    placeholder={currentQuestion?.placeholder || 'Escribí tu respuesta...'}
                    isOptional={shouldShowSkip}
                    fieldType={currentQuestion?.field}
                    onSkip={handleSkipField}
                    skipLabel={skipLabel}
                  />
                </motion.div>
              )}
            </AnimatePresence>
            {(mobileCtaConfig || mobileSkipConfig || mobileSummaryConfig) && (
              <div className={styles.mobileCtaBar}>
                {(mobileCtaConfig || mobileSkipConfig || mobileSummaryConfig) && (
                  <div className={styles.mobileCtaStep}>
                    Paso {(mobileCtaConfig ?? mobileSkipConfig ?? mobileSummaryConfig)?.step} de {totalSteps} · {(mobileCtaConfig ?? mobileSkipConfig ?? mobileSummaryConfig)?.label}
                  </div>
                )}
                {mobileCtaConfig && (
                  <button
                    className={styles.mobileCtaButton}
                    onClick={() => handleInteractiveAction(mobileCtaConfig.action)}
                    disabled={!mobileCtaConfig.enabled}
                    type="button"
                  >
                    Continuar
                  </button>
                )}
                {mobileSummaryConfig && (
                  <button
                    className={styles.mobileCtaButton}
                    onClick={() => handleInteractiveAction(mobileSummaryConfig.primaryAction)}
                    type="button"
                  >
                    Confirmar y finalizar
                  </button>
                )}
                {mobileSummaryConfig && (
                  <button
                    className={styles.mobileCtaButtonSecondary}
                    onClick={() => handleInteractiveAction(mobileSummaryConfig.secondaryAction)}
                    type="button"
                  >
                    Editar pasos
                  </button>
                )}
                {mobileSkipConfig && (
                  <button
                    className={styles.mobileCtaButtonSecondary}
                    onClick={handleSkipField}
                    type="button"
                  >
                    {skipLabel}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.sidebar}>
        <OnboardingNavigation 
          state={state} 
          editingStep={editingStep}
          currentInteractiveType={currentQuestion?.interactiveType}
          onNavigateToStep={(step: OnboardingStep) => navigateToStep(step)}
        />
        <ProfilePreview
          state={state}
          onRemoveSkill={removeSkill}
          onRemoveZone={(zoneId) => {
            if (zoneId === 'allCountry') {
              setAllCountry(false);
              setWorkCoverageKind('specificZones');
              return;
            }
            setWorkZones(state.workZones.filter((zone: any, index: number) => {
              if (typeof zoneId === 'number') {
                return (zone.id ?? index) !== zoneId;
              }
              return zone.id !== zoneId;
            }));
          }}
          onRemoveProfileCategory={removePreferredProfileCategory}
        />
        {showReturnToFinish && (
          <div className={styles.sidebarReturnToFinish}>
            <button
              type="button"
              className={styles.sidebarReturnToFinishButton}
              onClick={handleReturnToCompleteActions}
            >
              Cancelar edición
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationalOnboarding;
